import { z } from "zod";
import {
  defineBehavior,
  type BehaviorDefinition,
  type BehaviorPayloadAdapter,
} from "./types";

/**
 * The first behavior catalog: one record selection and one single-text-field
 * editor. Deliberately narrow, and code-reviewed, so the compiler has a real
 * registry to validate against before any App can use it.
 */

/** The shared editable-record payload the behaviors agree on. */
export const EditableRecordSchema = z.object({
  id: z.string().min(1).max(200),
  text: z.string().max(4000),
});
export type EditableRecord = z.infer<typeof EditableRecordSchema>;

/**
 * Maps a row or other payload shape onto the operation input both record
 * behaviors take, `{ record }`. The plan's template does the field mapping, so
 * the adapter validates the result rather than guessing at field names.
 */
export const EDITABLE_RECORD_ADAPTER: BehaviorPayloadAdapter = {
  id: "editable-record",
  description:
    "Validates { record: { id, text } }: the editable record an operation takes.",
  input: z.object({ record: EditableRecordSchema }),
};

const emptyConfig = z.object({}).strict();

export const RECORD_SELECTION = defineBehavior({
  id: "record-selection",
  version: 1,
  title: "Record selection",
  description:
    "Owns which record is selected. The view writes selection by targeting this instance, never by writing its state.",
  state: z.object({ selected: EditableRecordSchema.nullable().default(null) }),
  config: emptyConfig,
  operations: {
    select: {
      description: "Select one record and report it.",
      input: z.object({ record: EditableRecordSchema }),
      emits: { selected: z.object({ record: EditableRecordSchema }) },
      services: {},
      mutates: true,
    },
    clear: {
      description: "Clear the selection.",
      input: z.object({}),
      emits: {},
      services: {},
      mutates: true,
    },
  },
  publicEvents: ["selected"],
});

export const RECORD_EDITOR = defineBehavior({
  id: "record-editor",
  version: 1,
  title: "Record editor",
  description:
    "Edits one text field of one record, saves it with a bound command, and reports the saved record as a fact.",
  state: z.object({
    open: z.boolean().default(false),
    recordId: z.string().nullable().default(null),
    draft: z.string().default(""),
    saved: z.string().nullable().default(null),
    pending: z.boolean().default(false),
    error: z.string().nullable().default(null),
    /** Bumped on every open so a replacement cannot receive a retired result. */
    session: z.number().int().nonnegative().default(0),
  }),
  config: z.object({ fieldLabel: z.string().max(120).optional() }).strict(),
  operations: {
    open: {
      description:
        "Open the editor on a record with its current text as the draft.",
      input: z.object({ record: EditableRecordSchema }),
      emits: {},
      services: {},
      mutates: true,
    },
    changeDraft: {
      description: "Replace the unsaved draft.",
      input: z.object({ text: z.string().max(4000) }),
      emits: {},
      services: {},
      mutates: true,
    },
    save: {
      description:
        "Save the captured draft with the bound command, then emit the saved record.",
      input: z.object({}),
      emits: { saved: z.object({ record: EditableRecordSchema }) },
      services: {
        save: {
          params: ["id", "text"],
          description:
            "The command that persists one record. Its input reads { $param: id } and { $param: text }.",
        },
      },
      mutates: true,
    },
    close: {
      description: "Close the editor and retire its delivery session.",
      input: z.object({}),
      emits: {},
      services: {},
      mutates: true,
    },
  },
  publicEvents: ["saved"],
});

export const BEHAVIOR_DEFINITIONS: readonly BehaviorDefinition[] = [
  RECORD_SELECTION,
  RECORD_EDITOR,
];

export const BEHAVIOR_PAYLOAD_ADAPTERS: readonly BehaviorPayloadAdapter[] = [
  EDITABLE_RECORD_ADAPTER,
];

export function findBehaviorDefinition(
  id: string,
  version: number,
): BehaviorDefinition | null {
  return (
    BEHAVIOR_DEFINITIONS.find(
      (definition) => definition.id === id && definition.version === version,
    ) ?? null
  );
}

export function hasBehaviorDefinition(id: string): boolean {
  return BEHAVIOR_DEFINITIONS.some((definition) => definition.id === id);
}

export function findPayloadAdapter(id: string): BehaviorPayloadAdapter | null {
  return BEHAVIOR_PAYLOAD_ADAPTERS.find((adapter) => adapter.id === id) ?? null;
}

/** The state path one instance owns, `/$behaviors/<name>`. */
export function behaviorStatePath(name: string): string {
  return `/$behaviors/${name}`;
}

/** A definition's initial own state, validated. */
export function initialBehaviorState(
  definition: BehaviorDefinition,
): Record<string, unknown> {
  return definition.state.parse({});
}
